import React from 'react';
import {useTranslation} from 'react-i18next';
import {Link, useParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {Row, Col} from 'react-bootstrap';
import {BasicStepper, MainLoader} from '@components';
import {StyledH3} from '@styled';
import UserRegister from './UserRegister';
import CorporateRegister from './CorporateRegister';
import CompleteView from './CompleteView';
import {sagaActions} from './redux/saga';

const RegisterController = () => {
  const [t] = useTranslation();
  const {uuid} = useParams();
  const dispatch = useDispatch();
  const isLoading = useSelector(
    (state) => state.register.fetchUserByUUIDLoading
  );
  const userDetails = useSelector((state) => state.register.userDetails);
  const [isValidToken, toggleValidToken] = React.useState('pending');
  const [currentStep, setStepperStep] = React.useState(0);

  const stepsNav = React.useMemo(() => [
    {
      icon: <i className="fa fa-circle" style={{fontSize: '1.5rem'}} />,
      title: t('register.steps.userRegistration'),
      caption: t('register.steps.userRegistration'),
    },
    {
      icon: <i className="fa fa-circle" style={{fontSize: '1.5rem'}} />,
      title: t('register.steps.corporateRegistration'),
      caption: t('register.steps.corporateRegistration'),
    },
    {
      icon: <i className="fa fa-circle" style={{fontSize: '1.5rem'}} />,
      title: t('register.steps.completeRegistration'),
      caption: t('register.steps.completeRegistration'),
    },
  ]);

  const has2ShowSteps = (tokenStatus) => {
    const [status, scope] = tokenStatus.split('-');
    return status === 'valid' ? scope === 'corporate' : false;
  };

  const getTokenStatus = (tokenStatus) => {
    const [status] = tokenStatus.split('-');
    return status;
  };

  React.useEffect(() => {
    if (uuid) {
      // validate uuid first via api
      // if api is valid and not expired then only show the user registration form
      // other wise show the expired or invalid uuid or token
      dispatch({
        type: sagaActions.FETCH_USER_BY_UUID,
        payload: {
          values: {uuid},
        },
      });
    } else {
      toggleValidToken('error');
    }
  }, [uuid]);

  React.useEffect(() => {
    if (!isLoading && userDetails) {
      const isTokenValid = !userDetails.error ? true : false;
      const validTokenStr = isTokenValid
        ? `valid-${userDetails.InviteType.toLowerCase()}`
        : 'error';
      toggleValidToken(validTokenStr);

      if (isTokenValid && has2ShowSteps(validTokenStr)) {
        // then check already filled/saved the user details
        const isAlreadyFilledAndTermsAgreed =
          (userDetails.firstName && userDetails.terms) ||
          userDetails.existingUser
            ? true
            : false;
        if (isAlreadyFilledAndTermsAgreed) {
          // goto step 2 - corporate register form
          setStepperStep(1);
        }
      }
    }
  }, [isLoading, userDetails]);

  const handleGotoStep = React.useCallback(
    (step) => {
      if (has2ShowSteps(isValidToken)) {
        if (step > stepsNav.length) {
          setStepperStep(step);
        } else {
          setStepperStep(step);
        }
      } else {
        setStepperStep(2);
      }
    },
    [isValidToken]
  );

  document.getElementById('root').classList = 'hold-transition registration';

  return (
    <>
      {isLoading && (
        <MainLoader
          minHeight="100vh"
          spinnerColor="#479541"
          background="rgba(0,0,0,0.2)"
        />
      )}
      <Row>
        <Col xs={0} lg={2} className="d-none d-lg-block">
          <div
            className="cce-logo cce-brand-width d-none d-lg-flex"
            style={{justifyContent: 'flex-start'}}
          >
            <Link
              to="/"
              className="cce-logo-link"
              style={{padding: '11px 0 0 23px'}}
            >
              <span className="cce-logo-title" style={{fontWeight: 900}}>
                <span style={{fontSize: '24px', display: 'block'}}>CCE</span>
                <span style={{fontSize: '18px'}}>Data Portal</span>
              </span>
            </Link>
          </div>
        </Col>
        <Col xs={12} lg={8}>
          {getTokenStatus(isValidToken) === 'valid' && (
            <div style={{padding: '1rem 0'}}>
              {has2ShowSteps(isValidToken) ? (
                <BasicStepper
                  isNextClick={false}
                  disabledSteps={[]}
                  mainCaption=""
                  currentStep={currentStep}
                  setCurrentStep={() => {}}
                  steps={stepsNav}
                />
              ) : null}
              {currentStep === 0 && (
                <UserRegister handleNext={handleGotoStep} token={uuid} />
              )}
              {currentStep === 1 && (
                <CorporateRegister handleNext={handleGotoStep} token={uuid} />
              )}
              {currentStep === 2 && (
                <CompleteView
                  handleNext={handleGotoStep}
                  viewType={has2ShowSteps(isValidToken) ? 'corporate' : 'user'}
                />
              )}
            </div>
          )}
          {!isLoading && getTokenStatus(isValidToken) === 'error' ? (
            <div style={{padding: '1rem 0'}}>
              <div
                className="card w-lg-40 mx-auto"
                style={{minHeight: '200px'}}
              >
                <div className="card-body">
                  <StyledH3
                    className="mb-5 mt-0 text-center"
                    style={{color: 'var(--danger)'}}
                  >
                    <i className="fa fa-exclamation-circle mr-2" />
                    {t('register.invalidTokenTitle')}
                  </StyledH3>
                  <div className="text-center">
                    {t('register.invalidTokenDesc')}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
          {isLoading && (
            <div className="text-muted text-center" style={{padding: '1rem 0'}}>
              {t('register.pleaseWait')}
            </div>
          )}
        </Col>
        <Col xs={0} lg={2} className="d-none d-lg-block">
          {' '}
        </Col>
      </Row>
    </>
  );
};

export default RegisterController;
